/**
 * Mock photo data generator for testing PhotoItem2 and PhotoCollection2 components
 *
 * @module utils/mockPhotoData
 */

import type { PhotoItem2ViewModel } from '../components/PhotoItem2/PhotoItem2.types';

/**
 * Lorem Ipsum sentences for photo descriptions
 */
const LOREM_SENTENCES = [
  'Golden hour light filtering through autumn leaves.',
  'Misty morning reveals hidden forest pathways.',
  'Urban geometry captured in concrete and glass.',
  'Waves crash against weathered coastal rocks.',
  'Street vendor arranges colorful market produce.',
  'Snow-capped peaks pierce through morning clouds.',
  'Vintage bicycle leans against brick wall.',
  'Sunset paints the desert landscape orange.',
  'City lights reflect on rain-soaked pavement.',
  'Wildflowers bloom across mountain meadow.',
  'Ancient temple stands amid modern cityscape.',
  'Fisherman casts net at dawn.',
  'Autumn colors frame the winding path.',
  'Steam rises from morning coffee cup.',
  'Lone tree stands in empty field.',
];

/**
 * Mock camera models
 */
const CAMERA_MODELS = [
  'Canon EOS R5',
  'Nikon Z9',
  'Sony A7 IV',
  'Fujifilm X-T5',
  'Canon EOS 5D Mark IV',
  'Nikon D850',
  'Sony A1',
  'Leica M11',
];

/**
 * Mock lens models
 */
const LENS_MODELS = [
  'RF 24-70mm f/2.8L IS USM',
  'NIKKOR Z 70-200mm f/2.8 VR S',
  'FE 85mm f/1.4 GM',
  'XF 56mm f/1.2 R',
  'EF 50mm f/1.2L USM',
  'AF-S 24-120mm f/4G ED VR',
  'Summilux-M 50mm f/1.4 ASPH',
];

/**
 * Randomizer helper
 */
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Random element from array
 */
function randomElement<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Random boolean with probability
 */
function randomBool(probability = 0.5): boolean {
  return Math.random() < probability;
}

/**
 * Generate mock photo data for testing
 *
 * Creates PhotoItem2ViewModel objects with:
 * - Unique IDs
 * - Lorem Ipsum descriptions
 * - Random EXIF data
 * - Mock ratings and critique counts
 * - Multiple resolution URLs (using placeholder service)
 *
 * @param count - Number of mock photos to generate (default: 10)
 * @returns Array of mock PhotoItem2ViewModel objects
 *
 * @example
 * ```typescript
 * const photos = generateMockPhotoData(10);
 * <PhotoCollection2 photos={photos} />
 * ```
 */
export function generateMockPhotoData(count = 10): PhotoItem2ViewModel[] {
  return Array.from({ length: count }, (_, index) => {
    const numericId = Date.now() + index;
    const id = `ph_mock_${numericId}`;
    const width = randomInt(400, 1200);
    const height = randomInt(300, 900);

    // Use placeholder image service
    const baseUrl = `https://picsum.photos/seed/photo-${numericId}`;
    const thumbnailUrl = `${baseUrl}/300/200`;
    const storageUrl = `${baseUrl}/${width}/${height}`;

    // Build Photo object (PhotoItem2ViewModel now requires photoData wrapper)
    const photo: import('../types/photo').Photo = {
      id,
      userId: 'mock-user',
      title: `Photo ${index + 1}`,
      slug: `photo-${numericId}`,
      description: randomBool(0.8) ? randomElement(LOREM_SENTENCES) : null,
      filename: `photo-${numericId}.jpg`,
      fileSize: randomInt(500000, 5000000),
      mimeType: 'image/jpeg',
      width,
      height,
      metadata: randomBool(0.7) ? {
        camera: { model: randomElement(CAMERA_MODELS) },
        lens: { model: randomElement(LENS_MODELS) },
        focus: {
          focalLength: randomElement([24, 35, 50, 85, 135, 200]),
        },
        exposure: {
          fNumber: randomElement([1.4, 1.8, 2.8, 4.0, 5.6, 8.0]),
          exposureTime: `1/${randomElement([60, 125, 250, 500, 1000])}`,
          iso: randomElement([100, 200, 400, 800, 1600, 3200, 6400]),
        },
        capture: {
          dateTimeOriginal: new Date(
            Date.now() - randomInt(0, 365 * 24 * 60 * 60 * 1000)
          ).toISOString(),
        },
      } : null,
      variants: [
        {
          id: `var-thumb-${numericId}`,
          variantType: 'thumbnail',
          storageUrl: thumbnailUrl,
          width: 300,
          height: 200,
          fileSize: 10240,
          format: 'webp',
        },
        {
          id: `var-full-${numericId}`,
          variantType: 'full',
          storageUrl: storageUrl,
          width,
          height,
          fileSize: randomInt(500000, 5000000),
          format: 'jpeg',
        },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return {
      photoData: photo,
      aspectRatio: width / height,
      critiqueCount: randomInt(0, 50),
      averageCritiqueRating: randomBool(0.6)
        ? Math.round((randomInt(10, 50) / 10) * 10) / 10  // 1.0 to 5.0 in 0.1 increments
        : undefined,
      photographerRating: randomBool(0.5)
        ? Math.round((randomInt(10, 50) / 10) * 10) / 10  // 1.0 to 5.0 in 0.1 increments
        : undefined,
      selected: false,
    };
  });
}

/**
 * Generate large photo collection for stress testing
 *
 * Creates specified number of mock photos for testing 500-photo scenario.
 * Uses same generation logic as generateMockPhotoData but optimized for larger counts.
 *
 * @param count - Number of mock photos to generate (default: 500)
 * @returns Array of mock PhotoItem2ViewModel objects
 *
 * @example
 * ```typescript
 * const largeCollection = generateLargePhotoCollection(500);
 * <PhotoCollection2 photos={largeCollection} />
 * ```
 */
export function generateLargePhotoCollection(count = 500): PhotoItem2ViewModel[] {
  return generateMockPhotoData(count);
}

/**
 * Create a minimal test mock for PhotoItem2ViewModel with proper nested structure
 *
 * This is a lightweight helper for unit tests that need a simple mock object.
 * For richer mock data, use generateMockPhotoData() instead.
 *
 * @param overrides - Optional overrides for the Photo object
 * @returns PhotoItem2ViewModel with minimal valid structure
 *
 * @example
 * ```typescript
 * const mockData = createTestPhotoItem({
 *   id: 'photo-1',
 *   description: 'Test photo',
 * });
 * ```
 */
export function createTestPhotoItem(
  overrides: Partial<import('../types/photo').Photo> = {}
): PhotoItem2ViewModel {
  const defaultPhoto: import('../types/photo').Photo = {
    id: 'photo-test-1',
    globalId: 'phtest1',
    userId: 'user-test',
    title: 'Test Photo',
    slug: 'test-photo',
    description: 'Test photo description',
    filename: 'test.jpg',
    fileSize: 1024000,
    mimeType: 'image/jpeg',
    width: 1920,
    height: 1280,
    metadata: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    variants: [
      {
        id: 'var-thumb',
        variantType: 'thumbnail',
        storageUrl: 'https://example.com/thumb.jpg',
        width: 300,
        height: 200,
        fileSize: 10240,
        format: 'jpeg',
      },
      {
        id: 'var-medium',
        variantType: 'medium',
        storageUrl: 'https://example.com/medium.jpg',
        width: 1920,
        height: 1280,
        fileSize: 102400,
        format: 'jpeg',
      },
      {
        id: 'var-full',
        variantType: 'full',
        storageUrl: 'https://example.com/full.jpg',
        width: 6000,
        height: 4000,
        fileSize: 1024000,
        format: 'jpeg',
      },
    ],
  };

  return {
    photoData: {
      ...defaultPhoto,
      ...overrides,
    },
    critiqueCount: 0,
  };
}
